import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ChevronDown, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { BUDGET_OPTIONS, CONFIRMATION, SERVICE_OPTIONS } from "@/content";
import { EventDatePicker } from "@/components/site/EventDatePicker";

const EMPTY = { full_name: "", email: "", phone: "", organization: "", service: "", description: "", timeline: "", event_date: "", budget: "", additional: "" };

const fieldCls =
  "w-full border-0 border-b border-rc-line2 bg-transparent px-0 py-3 text-base text-rc-ink placeholder:text-rc-faint outline-none transition-[border-color] duration-300 focus:border-rc-copper";

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="font-mono-rc text-[11px] uppercase tracking-[0.22em] text-rc-muted">
      {label} {required && <span className="text-rc-copper">*</span>}
    </span>
    {children}
  </label>
);

const SelectField = ({ value, onChange, options, placeholder, testId, required }) => (
  <div className="relative">
    <select value={value} onChange={onChange} required={required} data-testid={testId} className={`${fieldCls} appearance-none pr-8 [&>option]:bg-rc-surface`}>
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
    <ChevronDown className="pointer-events-none absolute right-0 top-1/2 h-4 w-4 -translate-y-1/2 text-rc-copper" />
  </div>
);

const Success = ({ onReset }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
    className="flex min-h-[420px] flex-col justify-center"
    data-testid="inquiry-success"
  >
    <span className="font-mono-rc text-xs uppercase tracking-[0.28em] text-rc-copper">Inquiry received</span>
    <p className="mt-6 font-display text-3xl leading-snug text-rc-ink sm:text-4xl" data-testid="inquiry-success-message">{CONFIRMATION}</p>
    <button onClick={onReset} className="mt-10 w-fit text-sm text-rc-body underline underline-offset-4 hover:text-rc-copper" data-testid="inquiry-new-btn">
      Send another inquiry
    </button>
  </motion.div>
);

export const InquiryForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await api.post("/inquiries", form);
      setStatus("done");
      setForm(EMPTY);
    } catch (err) {
      setError(formatApiError(err));
      setStatus("idle");
    }
  };

  return (
    <AnimatePresence initial={false}>
      {status === "done" ? (
        <Success key="ok" onReset={() => setStatus("idle")} />
      ) : (
        <motion.form key="form" onSubmit={submit} className="grid grid-cols-1 gap-x-8 gap-y-8 sm:grid-cols-2" data-testid="inquiry-form">
          <Field label="Full name" required>
            <input required value={form.full_name} onChange={set("full_name")} className={fieldCls} placeholder="Your name" data-testid="inquiry-name-input" />
          </Field>
          <Field label="Email" required>
            <input required type="email" value={form.email} onChange={set("email")} className={fieldCls} placeholder="you@example.com" data-testid="inquiry-email-input" />
          </Field>
          <Field label="Phone" required>
            <input required type="tel" value={form.phone} onChange={set("phone")} className={fieldCls} placeholder="(000) 000-0000" data-testid="inquiry-phone-input" />
          </Field>
          <Field label="Church / organization / business">
            <input value={form.organization} onChange={set("organization")} className={fieldCls} placeholder="If applicable" data-testid="inquiry-org-input" />
          </Field>
          <Field label="Service of interest" required>
            <SelectField required value={form.service} onChange={set("service")} options={SERVICE_OPTIONS} placeholder="Select a service" testId="inquiry-service-select" />
          </Field>
          <Field label="Event date (if you have one)">
            <EventDatePicker value={form.event_date} onChange={(v) => setForm((f) => ({ ...f, event_date: v }))} />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Brief description of your project or needs" required>
              <textarea required rows={3} value={form.description} onChange={set("description")} className={`${fieldCls} resize-none`} placeholder="Tell us about your vision" data-testid="inquiry-description-input" />
            </Field>
          </div>
          <Field label="Desired timeline">
            <input value={form.timeline} onChange={set("timeline")} className={fieldCls} placeholder="e.g. Spring 2026, flexible" data-testid="inquiry-timeline-input" />
          </Field>
          <Field label="Budget range (optional)">
            <SelectField value={form.budget} onChange={set("budget")} options={BUDGET_OPTIONS} placeholder="Select a range" testId="inquiry-budget-select" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Additional details or questions">
              <input value={form.additional} onChange={set("additional")} className={fieldCls} placeholder="Anything else" data-testid="inquiry-additional-input" />
            </Field>
          </div>
          <div className="flex flex-col gap-4 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
            {error ? <p className="text-sm text-red-400" data-testid="inquiry-error">{error}</p> : <p className="text-sm text-rc-muted">We typically respond within a few business days.</p>}
            <button
              type="submit"
              disabled={status === "sending"}
              data-testid="inquiry-submit-btn"
              className="group flex w-fit items-center gap-3 rounded-full bg-rc-copper py-3.5 pl-7 pr-3.5 font-medium text-rc-bg transition-[background-color,transform] duration-300 hover:bg-rc-glow active:scale-95 disabled:opacity-60"
            >
              {status === "sending" ? "Sending…" : "Send inquiry"}
              <span className="grid h-8 w-8 place-items-center rounded-full bg-rc-bg text-rc-copper transition-transform duration-500 group-hover:rotate-45">
                {status === "sending" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4" />}
              </span>
            </button>
          </div>
        </motion.form>
      )}
    </AnimatePresence>
  );
};
